import React, { useEffect, useState } from "react";
import { ShieldCheck, MapPin, Plus, Trash2, AlertTriangle, Footprints, Pause, Play, Info, X, Home as HomeIcon, TreePine, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useSafeZoneTracker } from "@/hooks/useSafeZoneTracker";
import { Section, List, Row, Sheet } from "@/components/orio";

const TYPE_LABEL = {
  home: "Casa",
  park: "Parco",
  vet: "Veterinario",
  manual: "Personalizzata",
  auto: "Apprese da ORIO",
};

const TYPE_META = {
  home: { icon: HomeIcon, color: "#34D399" },
  park: { icon: TreePine, color: "#34D399" },
  vet: { icon: Stethoscope, color: "#FF3B30" },
  manual: { icon: ShieldCheck, color: "#FFB020" },
  auto: { icon: ShieldCheck, color: "#9aa0a6" },
};

export default function SafeZones() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { walkingMode, tracking, lastPos, activeAlert, setActiveAlert, start, stop } = useSafeZoneTracker();

  const load = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/safezones");
        if (!cancelled) {
          setZones(data.zones || []);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          if (e.response?.status !== 402) toast.error("Errore caricamento zone");
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  const removeZone = async (id) => {
    try {
      await api.delete(`/safezones/${id}`);
      toast.success("Zona rimossa");
      load();
    } catch {
      toast.error("Errore rimozione zona");
    }
  };

  return (
    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-32">
      {/* Header */}
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A] mb-2 inline-flex items-center gap-1.5">
        <ShieldCheck className="h-3 w-3" /> Sistema di prevenzione
      </div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">
        Anti-fuga <span className="orio-grad-text">invisibile</span>
      </h1>
      <p className="text-[#6B6B6B] mt-2 text-sm max-w-2xl">
        ORIO impara automaticamente le tue rotte abituali (casa, parco, veterinario) e ti avvisa
        se ti stai allontanando lasciando il cane indietro. La protezione che agisce
        <strong> prima </strong>che il cane sia tecnicamente perso.
      </p>

      {/* Walking mode hero */}
      <div
        className="mt-7 rounded-3xl border p-5 sm:p-6 relative overflow-hidden"
        style={{
          background: walkingMode
            ? "linear-gradient(180deg, rgba(52,211,153,0.18) 0%, rgba(52,211,153,0.08) 100%)"
            : "linear-gradient(180deg, rgba(28,28,30,0.65) 0%, rgba(14,14,16,0.7) 100%)",
          borderColor: walkingMode ? "rgba(52,211,153,0.45)" : "rgba(255,255,255,0.10)",
          backdropFilter: "blur(20px) saturate(160%)",
        }}
      >
        {walkingMode && (
          <div aria-hidden className="absolute -inset-1 -z-0 opacity-60"
            style={{ background: "radial-gradient(60% 60% at 20% 50%, rgba(52,211,153,0.22) 0%, rgba(0,0,0,0) 70%)" }} />
        )}
        <div className="relative flex items-start gap-4">
          <div
            className="h-12 w-12 rounded-2xl shrink-0 flex items-center justify-center"
            style={{
              background: walkingMode ? "rgba(52,211,153,0.25)" : "rgba(255,255,255,0.06)",
              color: walkingMode ? "#34D399" : "rgba(255,255,255,0.7)",
              border: `1px solid ${walkingMode ? "rgba(52,211,153,0.45)" : "rgba(255,255,255,0.08)"}`,
            }}
          >
            <Footprints className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A]">
              Modalità passeggiata
            </div>
            <div className="font-display text-xl mt-0.5">
              {walkingMode ? "Tracciamento attivo" : "Pronto a tracciare"}
            </div>
            <div className="text-[12px] text-[#9A9A9A] mt-1 leading-relaxed">
              {walkingMode
                ? "ORIO sta osservando la tua posizione. Se ti allontani da una zona sicura senza il cane, ti avviseremo."
                : "Attivalo quando esci con il cane. Tienilo spento per risparmiare batteria."}
            </div>
            {lastPos && walkingMode && (
              <div className="font-mono text-[10px] text-[#9A9A9A] mt-2.5 inline-flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#34D399] animate-pulse" />
                {lastPos.coords.latitude.toFixed(4)}, {lastPos.coords.longitude.toFixed(4)} · ±{Math.round(lastPos.coords.accuracy)}m
              </div>
            )}
          </div>
          <button
            data-testid="walk-toggle"
            onClick={walkingMode ? stop : start}
            className="px-4 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-all shrink-0"
            style={{
              background: walkingMode ? "rgba(255,255,255,0.10)" : "#fff",
              color: walkingMode ? "#fff" : "#000",
              border: walkingMode ? "1px solid rgba(255,255,255,0.18)" : "1px solid transparent",
            }}
          >
            {walkingMode ? <><Pause className="h-4 w-4" /> Stop</> : <><Play className="h-4 w-4" /> Avvia</>}
          </button>
        </div>
      </div>

      {/* Info row */}
      <div className="mt-4 rounded-2xl border border-[#F0EBE3] p-3.5 flex items-start gap-3"
        style={{ background: "rgba(255,255,255,0.02)" }}>
        <Info className="h-4 w-4 text-[#9A9A9A] shrink-0 mt-0.5" />
        <div className="text-[12px] text-[#6B6B6B] leading-relaxed">
          Il sistema funziona solo a <strong>app aperta</strong> sullo smartphone durante le passeggiate
          (limite tecnico delle PWA, in particolare su iOS). Apri ORIO quando esci con il cane.
        </div>
      </div>

      {/* Zones list */}
      <Section
        eyebrow="Le tue zone sicure"
        title="Casa, parco, veterinario"
        action={
          <button
            data-testid="add-zone"
            onClick={() => setShowAdd(true)}
            className="px-3.5 py-2 rounded-full bg-[#FAF7F0] hover:bg-white/[0.10] border border-[#F0EBE3] text-xs font-medium inline-flex items-center gap-1.5 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" /> Aggiungi
          </button>
        }
      >
        {loading ? (
          <div className="text-sm text-[#9A9A9A] py-6">Caricamento…</div>
        ) : zones.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-[#F0EBE3] p-10 text-center"
            style={{ background: "rgba(255,255,255,0.015)" }}>
            <MapPin className="h-8 w-8 text-[#C7C0B3] mx-auto mb-2" />
            <div className="text-[#1A1A1A] text-sm">Nessuna zona sicura ancora.</div>
            <div className="text-xs text-[#9A9A9A] mt-1 max-w-sm mx-auto">
              Aggiungine una manualmente o lascia che ORIO le impari uscendo con il cane.
            </div>
          </div>
        ) : (
          <List>
            {zones.map((z) => {
              const meta = TYPE_META[z.type] || TYPE_META.manual;
              const Icon = meta.icon;
              return (
                <div
                  key={z.id}
                  data-testid={`zone-${z.id}`}
                  className="flex items-start gap-3 px-4 sm:px-5 py-3.5"
                >
                  <div
                    className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
                    style={{
                      background: `${meta.color}1F`,
                      color: meta.color,
                      border: `1px solid ${meta.color}26`,
                    }}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] text-[#1A1A1A] leading-tight">{z.name}</div>
                    <div className="text-[11px] text-[#9A9A9A] mt-1 font-mono">
                      {z.lat.toFixed(4)}, {z.lng.toFixed(4)} · raggio {z.radius_m}m
                    </div>
                    <div className="mt-1.5 flex items-center gap-2 text-[10px]">
                      <span className="px-1.5 py-0.5 rounded bg-white/[0.05] text-[#9A9A9A] uppercase tracking-wider border border-[#F0EBE3]">
                        {TYPE_LABEL[z.type] || z.type}
                      </span>
                      {z.visits > 0 && (
                        <span className="text-[#9A9A9A]">{z.visits} visite</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => removeZone(z.id)}
                    data-testid={`delete-zone-${z.id}`}
                    className="h-8 w-8 rounded-lg bg-[#FAF7F0] hover:bg-[#FF3B30]/15 hover:text-[#FF3B30] flex items-center justify-center shrink-0 transition-colors"
                    aria-label="Elimina"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </List>
        )}
      </Section>

      <AddZoneSheet open={showAdd} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />

      {/* Escape alert sheet — keeps existing red-alert design */}
      {activeAlert && (
        <div
          className="fixed inset-0 z-[900] bg-black/85 backdrop-blur-md flex items-end sm:items-center justify-center p-4"
          data-testid="escape-alert-modal"
        >
          <div
            className="w-full max-w-md rounded-3xl p-5 sm:p-6 relative border"
            style={{
              background: "linear-gradient(180deg, rgba(20,8,8,0.96) 0%, rgba(10,4,4,0.98) 100%)",
              borderColor: "#FF3B30",
              boxShadow: "0 0 60px rgba(255,59,48,0.35)",
            }}
          >
            <button
              onClick={() => setActiveAlert(null)}
              data-testid="dismiss-escape-alert"
              className="absolute right-3 top-3 h-8 w-8 rounded-full bg-[#F4EFE6] hover:bg-[#F0EBE3] flex items-center justify-center"
            >
              <X className="h-4 w-4" />
            </button>
            <div
              className="h-12 w-12 rounded-2xl flex items-center justify-center"
              style={{ background: "rgba(255,59,48,0.22)", color: "#FF3B30" }}
            >
              <AlertTriangle className="h-6 w-6" />
            </div>
            <h2 className="font-display text-2xl tracking-tight mt-3" style={{ color: "#FF3B30" }}>
              {activeAlert.title}
            </h2>
            <p className="text-sm text-[#1A1A1A] mt-2">{activeAlert.message}</p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                onClick={() => setActiveAlert(null)}
                data-testid="alert-confirm-with-dog"
                className="px-4 py-2.5 rounded-full bg-white text-black font-semibold text-sm"
              >
                Sì, è con me
              </button>
              <button
                onClick={() => { setActiveAlert(null); window.location.href = "/app/emergenza"; }}
                data-testid="alert-trigger-sos"
                className="px-4 py-2.5 rounded-full font-semibold text-sm"
                style={{ background: "#FF3B30", color: "#fff" }}
              >
                NO — Allerta SOS
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddZoneSheet({ open, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("home");
  const [coords, setCoords] = useState(null);
  const [loading, setLoading] = useState(false);

  // reset on close
  useEffect(() => {
    if (!open) {
      setName("");
      setType("home");
      setCoords(null);
    }
  }, [open]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocalizzazione non disponibile");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); toast.success("Posizione acquisita"); },
      () => toast.error("Impossibile ottenere la posizione")
    );
  };

  const submit = async () => {
    if (!name.trim()) return toast.error("Dai un nome alla zona");
    if (!coords) return toast.error("Usa la tua posizione attuale");
    setLoading(true);
    try {
      await api.post("/safezones", { name: name.trim(), lat: coords.lat, lng: coords.lng, type, radius_m: 150 });
      toast.success("Zona sicura aggiunta");
      onSaved();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      eyebrow="Nuova zona sicura"
      title="Aggiungi un punto fisso"
      testid="add-zone-modal"
      footer={
        <button
          onClick={submit}
          disabled={loading}
          data-testid="zone-submit"
          className="relative w-full px-4 py-3 rounded-full orio-cta-btn font-semibold text-black disabled:opacity-50 overflow-hidden"
        >
          <span className="relative z-10">{loading ? "Salvataggio…" : "Salva zona sicura"}</span>
        </button>
      }
    >
      <p className="text-[12px] text-[#9A9A9A] leading-relaxed">
        Vai sul posto (casa, parco, veterinario), usa la tua posizione e salvala. ORIO te lo terrà a mente.
      </p>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#9A9A9A] mb-1.5">Nome zona</div>
        <input
          data-testid="zone-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Es. Casa, Parco Sempione, Vet Dr. Rossi"
          className="w-full px-3.5 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-white/30 text-sm transition-colors"
        />
      </div>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#9A9A9A] mb-1.5">Tipo</div>
        <div className="grid grid-cols-3 gap-2">
          {["home", "park", "vet"].map((t) => {
            const meta = TYPE_META[t];
            const Icon = meta.icon;
            const active = type === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                data-testid={`zone-type-${t}`}
                className="px-3 py-3 rounded-xl text-xs border transition-all flex flex-col items-center gap-1.5"
                style={{
                  background: active ? `${meta.color}1F` : "rgba(255,255,255,0.03)",
                  borderColor: active ? `${meta.color}55` : "rgba(255,255,255,0.10)",
                  color: active ? meta.color : "rgba(255,255,255,0.7)",
                }}
              >
                <Icon className="h-4 w-4" />
                {TYPE_LABEL[t]}
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        onClick={useMyLocation}
        data-testid="zone-geo"
        className="mt-4 w-full px-3.5 py-3 rounded-xl bg-[#FAF7F0] hover:bg-[#F4EFE6] border border-[#F0EBE3] text-xs inline-flex items-center justify-center gap-1.5 transition-colors"
      >
        <MapPin className="h-3.5 w-3.5" />
        {coords ? `Posizione: ${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}` : "Usa la mia posizione attuale"}
      </button>
    </Sheet>
  );
}
